import { Fragment, useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "./Dashboard";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
  Mail,
  MessageSquare,
  MessagesSquare,
  CalendarClock,
  Search,
  X,
} from "lucide-react";
import clsx from "clsx";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import AxiosInstance from "@/utils/AxiosInstance";
import { useOrg } from "@/contexts/useOrg";
import { useAuth } from "@/contexts/use-auth";
import { toast } from "sonner";
import { NoCarePlanMonthlyScheduleDialog } from "@/components/notifications/NoCarePlanMonthlyScheduleDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Link } from "react-router";
import { Pagination } from "@/components/Pagination";
import { format as formatDf, isValid as isValidDf, parseISO } from "date-fns";
import { TemplateVarsCell } from "@/components/TemplateVarsCell";
import { SmsBodyCell } from "@/components/SmsBodyCell";
import { TableSkeleton } from "@/components/ui/Skeleton/TableSkeleton";
import { formatUsTimezoneLabel } from "@/constants/usOrgTimezones";
import { formatUSPhoneNumber } from "@/utils/helpers";
import { Checkbox } from "@/components/ui/checkbox";
import AssignMultiSelect from "@/components/AssignMultiSelect";

/** Care-plan monthly `cycle` query param (`YYYY-MM`). */
function formatCycleYm(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const NOTIFICATION_TABLE_DATETIME_PATTERN = "MM-dd-yyyy hh:mm:ss a";

function parseNotificationTableDate(raw: unknown): Date | undefined {
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const d = new Date(raw);
    return isValidDf(d) ? d : undefined;
  }
  const s = String(raw).trim();
  if (!s || s === "—") return undefined;

  let d = parseISO(s);
  if (isValidDf(d)) return d;

  const spaced = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,3}))?/.exec(
    s,
  );
  if (spaced) {
    const [, y, mo, day, h, min, sec] = spaced;
    d = new Date(
      Number(y),
      Number(mo) - 1,
      Number(day),
      Number(h),
      Number(min),
      sec != null ? Number(sec) : 0,
    );
    if (isValidDf(d)) return d;
  }

  d = new Date(s);
  return isValidDf(d) ? d : undefined;
}

/** MM-DD-YYYY hh:mm:ss AM/PM for log / unsubscribe rows and parsed detail fields */
function formatNotificationTableDateTime(raw: unknown): string {
  const d = parseNotificationTableDate(raw);
  if (d) return formatDf(d, NOTIFICATION_TABLE_DATETIME_PATTERN);

  const s =
    typeof raw === "string"
      ? raw.trim()
      : raw === null || raw === undefined || raw === ""
        ? ""
        : String(raw).trim();
  if (!s || s === "—") return "—";
  return s;
}

function isUpcomingDetailDateLabel(label: string): boolean {
  const raw = label.trim();
  if (/\btimezone\b/i.test(raw)) return false;
  const lc = raw.toLowerCase();
  if (/\btime\b/.test(lc)) return true;
  const compact = lc.replace(/[\s_-]/g, "");
  return /^(runat|scheduledfor|scheduledat|dueat|createdat|updatedat|timestamp|sentat|unsubscribedat)$/.test(
    compact,
  );
}

function formatUpcomingDetailCell(label: string, value: string): string {
  const v = typeof value === "string" ? value.trim() : String(value ?? "").trim();
  if (!v || v === "—") return "—";
  if (!isUpcomingDetailDateLabel(label)) return v;
  const d = parseNotificationTableDate(v);
  if (d) return formatDf(d, NOTIFICATION_TABLE_DATETIME_PATTERN);
  return v;
}

/*
function chunkDetailRows<T>(items: T[]): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += 2) {
    rows.push(items.slice(i, i + 2));
  }
  return rows;
}
*/

/** Prefer explicit job fields so critical-reading `_id` is not sent by mistake to care-plan abort. */
function resolveAbortJobId(item: any, fromCriticalReadingAlert: boolean): string | undefined {
  const fromArray =
    Array.isArray(item?.jobIds) && item.jobIds.length > 0
      ? item.jobIds[0]
      : undefined;
  const explicit =
    item?.jobId ??
    item?.notificationJobId ??
    item?.pendingJobId ??
    fromArray;
  if (explicit != null && String(explicit).trim() !== "")
    return String(explicit).trim();
  if (!fromCriticalReadingAlert) {
    const id = item?._id ?? item?.id;
    if (id != null && String(id).trim() !== "") return String(id).trim();
  }
  return undefined;
}

function patientMongoProfilePath(
  patient: Record<string, unknown> | null | undefined,
): string | undefined {
  const oid = patient != null ? (patient as { _id?: unknown })._id : undefined;
  if (oid == null) return undefined;
  const s = String(oid).trim();
  if (!s) return undefined;
  return `/patients/${s}`;
}

/** Same as `FilterDropDowns` single-select (active) styling — filter row on Patients list. */
const notificationsFilterSelectTriggerClass =
  "h-9 rounded-lg font-medium text-sm px-3 gap-2 w-[min(100%,220px)] md:w-auto bg-primary-dark text-white hover:bg-primary border-0 shadow-none [&_svg]:!text-white [&_svg]:opacity-100";

type NotificationRecipientTypeFilter =
  | "all"
  | "caretaker"
  | "provider"
  | "practitioner"
  | "patient";

/** Email/SMS Completed & Sent logs: backed by `recipientType` query param. */
function RecipientTypeFilterSelect({
  value,
  onValueChange,
}: {
  value: NotificationRecipientTypeFilter;
  onValueChange: (v: NotificationRecipientTypeFilter) => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(v) => onValueChange(v as NotificationRecipientTypeFilter)}
    >
      <SelectTrigger
        aria-label="Recipient Type"
        className={notificationsFilterSelectTriggerClass}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Type</SelectItem>
        <SelectItem value="caretaker">Caretaker</SelectItem>
        <SelectItem value="provider">Provider</SelectItem>
        <SelectItem value="practitioner">Practitioner</SelectItem>
        <SelectItem value="patient">Patient</SelectItem>
      </SelectContent>
    </Select>
  );
}

/** `/critical-readings` alert document → Upcoming table row. */
function mapCriticalReadingAlert(item: any, fallbackIdx: number): UpcomingRow {
  const patient =
    typeof item?.patientId === "object" && item?.patientId != null
      ? item.patientId
      : {};
  const bizId =
    patient?.patientId ??
    patient?.impiloExternalIdentifier ??
    (typeof item?.patientId === "string" ? item.patientId : undefined) ??
    `row-${fallbackIdx}`;
  const name =
    [patient?.firstName, patient?.middleName, patient?.lastName].filter(Boolean).join(" ").trim() ||
    "—";

  const org =
    typeof item?.organizationId === "object" && item?.organizationId != null
      ? item.organizationId
      : {};
  const organisation = org?.organizationName ?? org?.groupName ?? "—";

  const facility =
    typeof item?.facilityId === "object" && item?.facilityId != null
      ? item.facilityId
      : {};

  const timezone =
    item?.resolvedTimezone ??
    org?.timezone ??
    facility?.timezone ??
    patient?.timezone ??
    "—";

  const programs = patient?.programs;
  const program =
    Array.isArray(programs) && programs.length > 0
      ? programs.map((p: string) => String(p).toUpperCase()).join(", ")
      : item?.deviceType != null
        ? `RPM (${String(item.deviceType).replace(/_/g, " ")})`
        : "RPM";

  const id = String(item?._id ?? item?.alertId ?? `${bizId}-${fallbackIdx}`);

  const reading =
    typeof item?.readingId === "object" && item?.readingId != null
      ? item.readingId
      : {};
  const rv = item?.readingValues ?? {};
  const pulse = rv.pulse ?? reading?.pulse;
  const spo2 = rv.spo2 ?? reading?.spo2;

  const extras: Array<{ label: string; value: string }> = [
    { label: "Alert ID", value: String(item?.alertId ?? "—") },
    { label: "Severity", value: String(item?.severity ?? "—") },
    { label: "Priority", value: String(item?.priority ?? "—") },
    { label: "Critical reason", value: String(item?.criticalReason ?? "—") },
    {
      label: "Device type",
      value: String(item?.deviceType ?? reading?.deviceType ?? "—"),
    },
  ];
  if (pulse != null) extras.push({ label: "Pulse", value: String(pulse) });
  if (spo2 != null) extras.push({ label: "SpO₂", value: String(spo2) });
  const readTs =
    reading?.readingDateLocal ?? reading?.readingDate ?? item?.createdAtLocal;
  if (readTs != null && readTs !== "")
    extras.push({ label: "Reading time", value: String(readTs) });
  extras.push({
    label: "Workflow status",
    value: String(item?.status ?? "—"),
  });
  if (item?.patientContacted != null)
    extras.push({
      label: "Patient contacted",
      value: String(item.patientContacted),
    });
  if (facility?.name != null && facility.name !== "")
    extras.push({ label: "Facility", value: String(facility.name) });

  return {
    id,
    channel: "alert",
    abortJobId: resolveAbortJobId(item, true),
    patientProfilePath:
      patientMongoProfilePath(patient as Record<string, unknown>),
    patientId: typeof bizId === "string" ? bizId : String(bizId),
    patientName: name,
    timezone,
    organisation,
    program,
    phoneNo: patient?.primaryNumber ?? patient?.phoneNumber ?? undefined,
    smsContent: undefined,
    smsType: undefined,
    timeSmsSent: undefined,
    deliveryStatus: undefined,
    emailType: undefined,
    timeEmailSent: undefined,
    status: undefined,
    expandedDetails: extras,
  };
}

/**
 * Maps an upcoming-queue item → table row.
 * Supports `GET .../care-plan-monthly/pending` rows (`pending[]`: `jobId`, `patient`, `scheduledFor`, …)
 * and legacy `/critical-readings`-style alerts (`alertId`, `criticalReason`, …).
 */
const UPCOMING_PENDING_DETAIL_LABELS: Record<string, string> = {
  runAt: "Run at",
  scheduledFor: "Scheduled for",
  cycle: "Billing cycle",
  dueAt: "Due at",
  scheduledAt: "Scheduled at",
  notes: "Notes",
  carePlanId: "Care plan ID",
  bucket: "Queue bucket",
};

/** `channels: { sms, email }` on care-plan monthly pending (fallback: job channel / legacy fields). */
function resolveCarePlanPendingChannel(item: any): UpcomingChannel {
  const flags = item?.channels;
  if (flags != null && typeof flags === "object") {
    const sms = !!(flags as { sms?: boolean })?.sms;
    const email = !!(flags as { email?: boolean })?.email;
    if (sms && !email) return "sms";
    if (email && !sms) return "email";
    if (sms && email) return "email,sms";
  }
  // const jobs = item?.jobs;
  // if (Array.isArray(jobs) && jobs.length > 0) {
  //   const jch = String(jobs[0]?.channel ?? "").toLowerCase();
  //   if (jch === "sms") return "sms";
  //   if (jch === "email") return "email";
  // }
  // const raw = String(
  //   item?.channel ?? item?.deliveryChannel ?? "email",
  // ).toLowerCase();
  // return raw === "sms" ? "sms" : raw === "email" ? "email" : "email,sms";

  return null;
}

function mapCarePlanMonthlyPending(item: any, fallbackIdx: number): UpcomingRow {
  const isCriticalAlert =
    item?.alertId != null ||
    (item?.criticalReason != null && item?.readingValues != null) ||
    (item?.severity != null &&
      item?.deviceType != null &&
      item?.criticalReason != null);
  if (isCriticalAlert) return mapCriticalReadingAlert(item, fallbackIdx);
  
  const patient =
    typeof item?.patientId === "object" && item?.patientId != null
      ? item.patientId
      : typeof item?.patient === "object" && item?.patient != null
        ? item.patient
        : {};
  const bizId =
    patient?.patientId ??
    patient?.businessId ??
    (typeof item?.patientId === "string" ? item.patientId : undefined) ??
    item?.patientRef ??
    item?.patientIdentifier ??
    `row-${fallbackIdx}`;
  const name =
    [patient?.firstName, patient?.middleName, patient?.lastName].filter(Boolean).join(" ").trim() ||
    patient?.displayName ||
    item?.patientName ||
    "—";

    const programName =  patient?.programs?.map((p: string) => String(p)?.toUpperCase())?.filter((p: string) => p === "CCM" || p === "PCM")?.join(", ") ?? "—";


  const channel: UpcomingChannel = resolveCarePlanPendingChannel(item);
   
  const organisation =
    item?.organizationName ??
    item?.patient?.organization?.organizationName ??
    item?.organizationId?.organizationName ??
    patient?.organizationName ??
    patient?.organisationName ??
    (typeof patient?.organizationId === "string" && patient.organizationId
      ? patient.organizationId
      : "—");
  const timezone = patient?.timezone ?? item?.timezone ?? "—";
  const program =
    item?.program ?? item?.programName ?? patient?.programName ?? programName ?? "—";

  const id = String(
    item?.jobId ?? item?._id ?? item?.id ?? `${bizId}-${fallbackIdx}`,
  );

  const sr = String(
    item?.status ?? item?.emailStatus ?? item?.jobStatus ?? "pending",
  ).toLowerCase();
  const statusMap: Record<string, UpcomingEmailStatus> = {
    pending: "pending",
    sent: "sent",
    error: "error",
    aborted: "abort",
    abort: "abort",
  };
  const status = statusMap[sr] ?? "pending";

  const extras: Array<{ label: string; value: string }> = [];
  if (item && typeof item === "object") {
    for (const key of [
      "runAt",
      "scheduledFor",
      "cycle",
      "dueAt",
      "scheduledAt",
      "notes",
      "carePlanId",
      "bucket",
    ]) {
      if (item[key] != null && item[key] !== "")
        extras.push({
          label: UPCOMING_PENDING_DETAIL_LABELS[key] ?? key,
          value: String(item[key]),
        });
    }
    if (patient?.status != null && patient.status !== "") {
      extras.push({ label: "Patient status", value: String(patient.status) });
    }
    if (item?.channels != null && typeof item.channels === "object") {
      const sms = !!(item.channels as { sms?: unknown }).sms;
      const email = !!(item.channels as { email?: unknown }).email;
      const parts = [
        ...(sms ? ["SMS"] : []),
        ...(email ? ["Email"] : []),
      ];
      if (parts.length > 0) {
        extras.push({ label: "Channels", value: parts.join(", ") });
      }
    }
  }

  return {
    id,
    channel,
    abortJobId: resolveAbortJobId(item, false),
    patientProfilePath:
      patientMongoProfilePath(patient as Record<string, unknown>),
    patientId: typeof bizId === "string" ? bizId : String(bizId),
    patientName: name,
    timezone,
    email : patient?.email ?? item?.email ?? "—",
    organisation,
    program,
    phoneNo:
      patient?.primaryNumber ?? patient?.phoneNumber ?? item?.phoneNo ?? undefined,
    smsContent: item?.smsBody ?? item?.smsContent ?? item?.smsPreview,
    smsType: item?.smsType ?? item?.smsTemplateName,
    timeSmsSent:
      item?.timeSmsSent ??
      item?.smsScheduledAt ??
      (channel === "sms"
        ? item?.runAt ?? item?.scheduledFor
        : undefined),
    deliveryStatus: item?.deliveryStatus ?? item?.smsStatus,
    emailType: item?.emailType ?? item?.emailTemplateName ?? "Care plan monthly",
    timeEmailSent:
      item?.timeEmailSent ??
      item?.emailScheduledAt ??
      item?.scheduledAt ??
      item?.scheduledFor ??
      item?.runAt,
    status,
    expandedDetails:
      extras.length > 0
        ? extras
        : undefined,
  };
}

/** Match Patients list (`TableWithSearch` + `MyTable`) typography and borders. */
const tableShellClass = "overflow-hidden rounded-md border border-grey pb-2";
const tableHeaderRowClass = "";
const tableHeadClass = "font-semibold teachers whitespace-nowrap";
const tableBodyRowClass = "";
const tableCellClass = "text-sm text-tertiary font-[500] teachers";
const tableIdLinkClass =
  "text-primary hover:underline font-medium teachers cursor-pointer";

type NotificationRow = {
  patientId: string;
  name: string;
  phoneNumber: string;
  email: string;
  organisation: string;
  program: string;
  timezone: string;
  smsTemplateName: string;
  emailTemplateName: string;
  smsBody: string;
  recipientType?: string;
  emailBody: string;
  recipientEmail?: string;
  /** Delivery / log status from API (`log.status`) */
  status: string;
  timestamp: string;
  /** Log `_id` for stable table keys */
  rowKey?: string;
  /** Recipient detail route (Mongo `_id` in URL; business id shown in `patientId` column) */
  detailPathname?: string;
  /** Email template merge fields from log API; shape varies by template. */
  templateVars?: unknown;
};

type EmailUnsubscribeRow = {
  id: string;
  auditMongoId?: string | undefined;
  patientId: string;
  name: string;
  email: string;
  /** Present when audit includes a phone on the entity */
  phone?: string;
  unsubscribedAt: string;
  organisation: string;
  status: string;
  channel?: string;
  recipientRole?: string;
  /** Profile URL built from audit `entityId` (Mongo) + role/kind — see email-unsubscribe audit response */
  detailPathname?: string;
  /** Snapshot of merge fields from the related notification. */
  templateVars?: unknown;
};

type UpcomingChannel = "email" | "sms" | "alert" | "email,sms" | null;
type UpcomingEmailStatus = "pending" | "sent" | "error" | "abort";

type UpcomingRow = {
  id: string;
  channel: UpcomingChannel;
  /** For `POST /notifications/admin/care-plan-monthly/abort` `{ jobIds: [abortJobId] }`. */
  abortJobId?: string;
  patientId: string;
  patientName: string;
  timezone: string;
  organisation: string;
  program: string;
  phoneNo?: string;
  smsContent?: string;
  smsType?: string;
  timeSmsSent?: string;
  deliveryStatus?: string;
  emailType?: string;
  timeEmailSent?: string;
  email?: string;
  status?: UpcomingEmailStatus;
  /** Patient profile route when Mongo `_id` known (`patient` on job / alert). */
  patientProfilePath?: string;
  /** Expandable details (can be many rows). UI-only. */
  expandedDetails?: Array<{ label: string; value: string }>;
};

const MOCK_ROWS: NotificationRow[] = [
  {
    patientId: "PT-100012",
    name: "Alex Johnson",
    phoneNumber: "+1 (555) 012-3011",
    email: "alex.johnson@example.com",
    organisation: "Anthro Care",
    program: "RPM",
    timezone: "America/Chicago",
    smsTemplateName: "RPM Reminder",
    emailTemplateName: "RPM Reminder Email",
    smsBody: "Hi Alex, please submit your RPM reading today.",
    emailBody:
      "Hello Alex,\n\nThis is a reminder to submit your RPM reading today.\n\nThanks,\nCare Team",
    status: "sent",
    timestamp: "2026-05-04 09:10",
  },
  {
    patientId: "PT-100089",
    name: "Priya Sharma",
    phoneNumber: "+1 (555) 010-8891",
    email: "priya.sharma@example.com",
    organisation: "Anthro Care",
    program: "CCM",
    timezone: "America/New_York",
    smsTemplateName: "Care Plan Monthly",
    emailTemplateName: "Care Plan Monthly Email",
    smsBody: "Hi Priya, your monthly care plan is ready to review.",
    emailBody:
      "Hi Priya,\n\nYour monthly care plan is ready to review.\n\nRegards,\nCare Team",
    status: "sent",
    timestamp: "2026-05-03 18:42",
  },
];

/**
 * EMAIL/SMS log row: show business IDs (PAT_*, PROV_*, …), navigate with Mongo `_id`
 * (`/notifications/admin/logs/email` shaped like sample `Untitled-5`).
 */
function resolveNotificationLogRecipientRoute(log: any): {
  displayId: string;
  pathname: string;
} | null {
  if (typeof log?.patientId === "object" && log.patientId?._id != null) {
    const ent = log.patientId;
    const display =
      ent.patientId ??
      ent.impiloExternalIdentifier ??
      String(ent._id);
    return { displayId: String(display), pathname: `/patients/${ent._id}` };
  }
  if (typeof log?.providerId === "object" && log.providerId?._id != null) {
    const ent = log.providerId;
    return {
      displayId: String(ent.providerId ?? ent._id),
      pathname: `/providers/${ent._id}`,
    };
  }
  if (typeof log?.practitionerId === "object" && log.practitionerId?._id != null) {
    const ent = log.practitionerId;
    return {
      displayId: String(ent.practitionerId ?? ent._id),
      pathname: `/practitioners/${ent._id}`,
    };
  }
  if (typeof log?.caretakerId === "object" && log.caretakerId?._id != null) {
    const ent = log.caretakerId;
    return {
      displayId: String(ent.caretakerId ?? ent._id),
      pathname: `/caretakers/${ent._id}`,
    };
  }

  const key = String(
    log?.metadata?.lifecycle?.entityKey ??
      log?.recipientType ??
      log?.recipientRole ??
      "",
  ).toLowerCase();
  const oid = log?.metadata?.lifecycle?.entityObjectId;
  if (typeof oid === "string" && oid.length > 0) {
    if (key === "patient")
      return { displayId: oid, pathname: `/patients/${oid}` };
    if (key === "provider")
      return { displayId: oid, pathname: `/providers/${oid}` };
    if (key === "practitioner")
      return { displayId: oid, pathname: `/practitioners/${oid}` };
    if (key === "caretaker")
      return { displayId: oid, pathname: `/caretakers/${oid}` };
  }

  return null;
}

function mapNotificationLogToRow(
  log: any,
  channel: "email" | "sms",
): NotificationRow {
  const recipientTypeRaw = (log?.recipientType ?? log?.recipientRole ?? "")
    .toString()
    .toLowerCase();
    const timezone = log?.timezone ?? log?.recipientTimezone ?? "—";
  const recipientType =
    recipientTypeRaw === "provider" ||
    recipientTypeRaw === "practitioner" ||
    recipientTypeRaw === "patient" ||
    recipientTypeRaw === "caretaker"
      ? recipientTypeRaw
      : "";
  const recipientObjectKey = recipientType ? `${recipientType}Id` : "";
  const recipient =
    (recipientObjectKey ? log?.[recipientObjectKey] : undefined) ??
    log?.patientId ??
    log?.providerId ??
    log?.practitionerId ??
    log?.caretakerId ??
    log?.patient ??
    {};
  const user = log?.userId ?? {};
  const templateVars = log?.templateVars ?? {};
  const meta = log?.metadata ?? log?.meta ?? {};
  const stripHtml = (value: string) =>
    value?.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const recipientName = [recipient?.firstName, recipient?.middleName, recipient?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  const route = resolveNotificationLogRecipientRoute(log);

  const displayIdFallback =
    recipient?.patientId ??
    recipient?.providerId ??
    recipient?.practitionerId ??
    recipient?.caretakerId ??
    recipient?._id ??
    user?._id ??
    (typeof log?.patientId === "string" ? log.patientId : undefined) ??
    log?.patient_id ??
    log?._id ??
    "—";

  return {
    patientId: route?.displayId ?? String(displayIdFallback),
    detailPathname: route?.pathname,
    rowKey:
      log?._id !== undefined && log?._id !== null ? String(log._id) : undefined,
    name:
      recipientName ||
      recipient?.name ||
      templateVars?.name ||
      user?.role ||
      log?.patientName ||
      "—",
    phoneNumber:
    recipient?.recipientPhone ??
    recipient?.recipientPhone ??
    recipient?.recipientPhone ??
    user?.recipientPhone ??
    log?.recipientPhone ??      
      recipient?.primaryNumber ??
      recipient?.phoneNumber ??
      recipient?.phone ??
      user?.phoneNumber ??
      log?.phoneNumber ??
      "—",
    email:recipient?.recipientEmail ?? user?.recipientEmail ??  log?.recipientEmail ??  recipient?.email ?? user?.email ?? log?.email ?? "—",
    organisation:
      log?.organizationId?.organizationName ??
      recipient?.organisationName ??
      recipient?.organizationName ??
      templateVars?.organizationName ??
      log?.organisation ??
      log?.organization ??
      "—",
    program: recipient?.programName ?? log?.eventType ?? log?.program ?? "—",
    timezone: timezone,
    smsTemplateName:
      log?.smsTemplateName ??
      meta?.smsDelivery?.registryKey ??
      log?.smsTemplate ??
      meta?.smsTemplate ??
      log?.eventType ??
      "—",
    emailTemplateName:
      channel === "email"
        ? log?.emailTemplateName ??
          meta?.emailDelivery?.registryKey ??
          log?.templateName ??
          log?.eventType ??
          meta?.emailTemplate ??
          "—"
        : "—",
    smsBody:
      stripHtml(log?.metadata?.body ?? "") || "—",
    recipientType: log?.recipientType ?? log?.recipientRole ?? "—",
    emailBody:
      channel === "email"
        ? stripHtml(log?.emailBody ?? log?.body ?? meta?.html ?? meta?.emailBody ?? "") ||
          "—"
        : "—",
    status: log?.status != null ? String(log.status) : "—",
    timestamp:
      log?.timestamp ??
      log?.createdAtLocal ??
      log?.created_at ??
      log?.sentAt ??
      "—",
    templateVars: log?.templateVars,
  };
}

/** Parse list + paging meta from `/notifications/admin/*` envelopes. */
function extractNotificationPagedItems(
  data: any,
  limit: number,
  currentPage: number,
): { items: any[]; totalPages: number } {
  const envelope = data?.data ?? data;
  const pickList = (): any[] => {
    if (Array.isArray(envelope)) return envelope;
    for (const key of [
      "logs",
      "items",
      "pending",
      "unsubscribes",
      "records",
      "notifications",
      "alerts",
    ]) {
      const v = envelope?.[key];
      if (Array.isArray(v)) return v;
    }
    for (const key of ["logs", "items"]) {
      const v = data?.[key];
      if (Array.isArray(v)) return v;
    }
    return [];
  };
  const items = pickList();
  const meta =
    envelope?.pagination ??
    envelope?.meta ??
    data?.pagination ??
    data?.meta ??
    {};

  let totalPages: number | undefined =
    typeof envelope?.totalPages === "number" && envelope.totalPages > 0
      ? envelope.totalPages
      : typeof meta.totalPages === "number" && meta.totalPages > 0
        ? meta.totalPages
        : typeof meta.pages === "number" && meta.pages > 0
          ? meta.pages
          : undefined;

  const total =
    meta.total ??
    meta.totalCount ??
    envelope?.totalCount ??
    envelope?.total ??
    data?.totalCount ??
    data?.total;

  if (
    totalPages == null &&
    total != null &&
    Number.isFinite(Number(total))
  ) {
    totalPages = Math.max(1, Math.ceil(Number(total) / limit));
  }

  if (totalPages == null) {
    if (items.length === 0)
      totalPages =
        currentPage > 1 ? Math.max(1, currentPage - 1) : 1;
    else if (items.length < limit) totalPages = currentPage;
    else totalPages = currentPage + 1;
  }

  return { items, totalPages: Math.max(1, totalPages) };
}

/**
 * `/notifications/admin/...unsubscribes` audit rows: UI shows `entity.businessId`, navigation uses Mongo `entityId`.
 */
function resolveUnsubscribeAuditDetailPath(item: any): string | undefined {
  const mongoId =
    item?.entityId ?? item?.entity?._id ?? item?.entityObjectId ?? undefined;
  if (mongoId == null || String(mongoId).trim() === "") return undefined;
  const id = String(mongoId).trim();

  const kind = String(
    item?.entity?.kind ??
      item?.recipientRole ??
      item?.recipientType ??
      "",
  ).toLowerCase();

  switch (kind) {
    case "patient":
      return `/patients/${id}`;
    case "provider":
      return `/providers/${id}`;
    case "practitioner":
      return `/practitioners/${id}`;
    case "caretaker":
    case "care_taker":
      return `/caretakers/${id}`;
    default:
      return undefined;
  }
}

/** Maps `/notifications/admin/email-unsubscribes` audit `logs[]` item (see API). */
function mapUnsubscribeAuditLog(item: any): EmailUnsubscribeRow {
  const ent = item?.entity ?? {};
  const businessId = ent?.businessId ?? item?.businessId;
  const id = String(item?._id ?? item?.id ?? "");
  const displayId =
    (typeof businessId === "string" && businessId) ||
    (item?.entityId != null ? String(item.entityId) : "") ||
    "—";
  const name =
    ent?.displayName ?? item?.displayName ?? item?.name ?? "—";
  const email = ent?.email ?? item?.email ?? "—";
  const phone =
    ent?.primaryNumber ??
    ent?.phoneNumber ??
    ent?.phone ??
    item?.primaryNumber ??
    item?.phoneNumber ??
    "";

  const organisation =
    item?.organizationName ??
    (typeof item?.organizationId === "object" && item?.organizationId?.organizationName) ??
    ent?.organizationName ??
    item?.organisationName ??
    "—";

  const unsubscribedAt =
    item?.unsubscribedAt ??
    item?.createdAt ??
    item?.updatedAt ??
    item?.timestamp ??
    "—";

  const status = String(
    item?.status ?? item?.subscriptionStatus ?? "unsubscribed",
  );

  const detailPathname = resolveUnsubscribeAuditDetailPath(item);

  const auditMongoId = item?._id;

  return {
    id: id || `${displayId}-${unsubscribedAt}`,
    auditMongoId,
    patientId: displayId,
    name,
    email,
    phone: phone ? String(phone) : undefined,
    unsubscribedAt,
    organisation,
    status,
    channel: item?.channel,
    recipientRole: item?.recipientRole,
    detailPathname,
    templateVars: item?.templateVars,
  };
}

type NotificationRecipientLinkRow = {
  patientId?: string;
  detailPathname?: string;
  recipientType?: string;
};

/** Logged-in caretakers should not navigate to their own profile from notification ID links. */
function isOwnCaretakerNotificationRow(
  row: NotificationRecipientLinkRow,
  user: { role?: string; id?: string; caretakerId?: string | null } | undefined,
  profile: { _id?: string; caretakerId?: string } | null | undefined,
): boolean {
  if (user?.role !== "caretaker") return false;

  const isCaretakerRow =
    row.recipientType?.toLowerCase() === "caretaker" ||
    row.detailPathname?.startsWith("/caretakers/");

  if (!isCaretakerRow) return false;

  const mongoFromPath = row.detailPathname?.match(/^\/caretakers\/([^/]+)/)?.[1];
  const profileMongoId = profile?._id != null ? String(profile._id) : "";
  const userMongoId = user?.id != null ? String(user.id) : "";

  if (
    mongoFromPath &&
    (mongoFromPath === profileMongoId || mongoFromPath === userMongoId)
  ) {
    return true;
  }

  const profileCaretakerId =
    profile?.caretakerId != null ? String(profile.caretakerId) : "";
  const userCaretakerId =
    user?.caretakerId != null ? String(user.caretakerId) : "";

  if (row.patientId) {
    const displayId = String(row.patientId);
    if (
      (userCaretakerId && displayId === userCaretakerId) ||
      (profileCaretakerId && displayId === profileCaretakerId)
    ) {
      return true;
    }
  }

  return false;
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-md border border-grey py-10 text-center text-sm text-tertiary font-[500] teachers">
      {text}
    </div>
  );
}

export default function Notifications() {
  const { selectedOrgId, organizations } = useOrg();
  const { user, profile } = useAuth();
  const isAdmin = user?.role === "admin";
  const isAdminManager = user?.role === "manager" && user?.managerScope === "tenant";

  const [tab, setTab] = useState<"email" | "sms" | "upcoming">(
    "email",
  );

  const [emailTab, setEmailTab] = useState<"completed" | "unsubscribed">(
    "completed",
  );
  const [emailRecipientType, setEmailRecipientType] =
    useState<NotificationRecipientTypeFilter>("all");
  const [smsRecipientType, setSmsRecipientType] =
    useState<NotificationRecipientTypeFilter>("all");
  const [smsTab, setSmsTab] = useState<"sent" | "unsubscribed">("sent");

  // Search scope: only inside the current Email / SMS / Queued tab (not global).
  const [emailSearch, setEmailSearch] = useState("");
  const [smsSearch, setSmsSearch] = useState("");
  const [upcomingSearch, setUpcomingSearch] = useState("");
  const [emailRows, setEmailRows] = useState<NotificationRow[]>([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [smsRows, setSmsRows] = useState<NotificationRow[]>([]);
  const [smsLoading, setSmsLoading] = useState(false);
  const [emailUnsubscribeRows, setEmailUnsubscribeRows] = useState<
    EmailUnsubscribeRow[]
  >([]);
  const [emailUnsubscribeLoading, setEmailUnsubscribeLoading] = useState(false);
  const [smsUnsubscribeRows, setSmsUnsubscribeRows] = useState<
    EmailUnsubscribeRow[]
  >([]);
  const [smsUnsubscribeLoading, setSmsUnsubscribeLoading] = useState(false);
  const [upcomingRows, setUpcomingRows] = useState<UpcomingRow[]>([]);
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  const [upcomingCycle, setUpcomingCycle] = useState(() => formatCycleYm(new Date()));
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [upcomingLimit, setUpcomingLimit] = useState(50);
  const [upcomingTotalPages, setUpcomingTotalPages] = useState(1);
  const [noCarePlanScheduleOpen, setNoCarePlanScheduleOpen] = useState(false);
  const [upcomingTriggerOrgIds, setUpcomingTriggerOrgIds] = useState<string[]>([]);
  const [upcomingTriggerOrgIdsDraft, setUpcomingTriggerOrgIdsDraft] = useState<
    string[]
  >([]);
  const [upcomingRefetchNonce, setUpcomingRefetchNonce] = useState(0);
  const [upcomingBulkSending, setUpcomingBulkSending] = useState(false);
  const [upcomingSelectedJobIdsByRowId, setUpcomingSelectedJobIdsByRowId] =
    useState<Record<string, string>>({});

  const effectiveUpcomingOrgIds = useMemo(
    () =>
      (isAdmin || isAdminManager)
        ? upcomingTriggerOrgIds
        : selectedOrgId
          ? [selectedOrgId]
          : [],
    [isAdmin, isAdminManager, upcomingTriggerOrgIds, selectedOrgId],
  );

  /** Backend search (debounced) for Email / SMS / Queued scoped lists */
  const [debouncedEmailSearch, setDebouncedEmailSearch] = useState("");
  const [debouncedSmsSearch, setDebouncedSmsSearch] = useState("");
  const [debouncedUpcomingSearch, setDebouncedUpcomingSearch] = useState("");

  const [emailLogPage, setEmailLogPage] = useState(1);
  const [emailLogLimit, setEmailLogLimit] = useState(10);
  const [emailLogTotalPages, setEmailLogTotalPages] = useState(1);

  const [smsLogPage, setSmsLogPage] = useState(1);
  const [smsLogLimit, setSmsLogLimit] = useState(10);
  const [smsLogTotalPages, setSmsLogTotalPages] = useState(1);

  const [emailUnsubPage, setEmailUnsubPage] = useState(1);
  const [emailUnsubLimit, setEmailUnsubLimit] = useState(10);
  const [emailUnsubTotalPages, setEmailUnsubTotalPages] = useState(1);

  const [smsUnsubPage, setSmsUnsubPage] = useState(1);
  const [smsUnsubLimit, setSmsUnsubLimit] = useState(10);
  const [smsUnsubTotalPages, setSmsUnsubTotalPages] = useState(1);

  /** Increment after a successful unsubscribe reverse to re-run the list GET. */
  const [emailUnsubRefetchNonce, setEmailUnsubRefetchNonce] = useState(0);
  const [smsUnsubRefetchNonce, setSmsUnsubRefetchNonce] = useState(0);

  useEffect(() => {
    const id = window.setTimeout(
      () => setDebouncedEmailSearch(emailSearch.trim()),
      400,
    );
    return () => window.clearTimeout(id);
  }, [emailSearch]);

  useEffect(() => {
    const id = window.setTimeout(
      () => setDebouncedSmsSearch(smsSearch.trim()),
      400,
    );
    return () => window.clearTimeout(id);
  }, [smsSearch]);

  useEffect(() => {
    const id = window.setTimeout(
      () => setDebouncedUpcomingSearch(upcomingSearch.trim()),
      400,
    );
    return () => window.clearTimeout(id);
  }, [upcomingSearch]);

  useEffect(() => {
    setEmailLogPage(1);
    setEmailUnsubPage(1);
  }, [debouncedEmailSearch]);

  useEffect(() => {
    setSmsLogPage(1);
    setSmsUnsubPage(1);
  }, [debouncedSmsSearch]);

  useEffect(() => {
    setUpcomingPage(1);
  }, [debouncedUpcomingSearch]);

  useEffect(() => {
    setEmailLogPage(1);
  }, [emailRecipientType]);

  useEffect(() => {
    setSmsLogPage(1);
  }, [smsRecipientType]);

  useEffect(() => {
    setEmailLogPage(1);
    setEmailUnsubPage(1);
  }, [emailTab]);

  useEffect(() => {
    setSmsLogPage(1);
    setSmsUnsubPage(1);
  }, [smsTab]);

  useEffect(() => {
    setUpcomingPage(1);
  }, [upcomingCycle, selectedOrgId, upcomingTriggerOrgIds]);

  useEffect(() => {
    setUpcomingTriggerOrgIdsDraft(upcomingTriggerOrgIds);
  }, [upcomingTriggerOrgIds]);

  /** Billing cycle months through the current month (no future cycles). */
  const upcomingCycleMonthOptions = useMemo(() => {
    const now = new Date();
    const maxCycle = formatCycleYm(now);
    const byValue = new Map<string, string>();
    for (let i = -18; i <= 0; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const value = formatCycleYm(d);
      byValue.set(
        value,
        d.toLocaleDateString(undefined, { month: "long", year: "numeric" }),
      );
    }
    if (
      upcomingCycle &&
      /^\d{4}-\d{2}$/.test(upcomingCycle) &&
      upcomingCycle <= maxCycle &&
      !byValue.has(upcomingCycle)
    ) {
      const parts = upcomingCycle.split("-").map(Number);
      const y = parts[0];
      const m = parts[1];
      if (y != null && m != null) {
        const d = new Date(y, m - 1, 1);
        if (!Number.isNaN(d.getTime())) {
          byValue.set(
            upcomingCycle,
            d.toLocaleDateString(undefined, { month: "long", year: "numeric" }),
          );
        }
      }
    }
    return Array.from(byValue.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([value, label]) => ({ value, label }));
  }, [upcomingCycle]);

  useEffect(() => {
    const maxCycle = formatCycleYm(new Date());
    if (upcomingCycle > maxCycle) {
      setUpcomingCycle(maxCycle);
    }
  }, [upcomingCycle]);

  async function abortUpcomingCarePlanMonthlyJob(row: UpcomingRow) {
    if (!row.abortJobId) return;
    try {
      await AxiosInstance().post(
        "/notifications/admin/care-plan-monthly/abort",
        { jobIds: [row.abortJobId] },
      );
      toast.success("Notification job removed.");
      setUpcomingRows((prev) => prev.filter((r) => r.id !== row.id));
      setUpcomingSelectedJobIdsByRowId((prev) => {
        if (!(row.id in prev)) return prev;
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ??
        e?.response?.data?.error ??
        e?.message ??
        "Could not abort job.";
      toast.error(typeof msg === "string" ? msg : "Could not abort job.");
    }
  }

  async function sendUpcomingCarePlanMonthlyJobs(jobIds: string[]) {
    if (!jobIds.length || upcomingBulkSending) return;
    try {
      setUpcomingBulkSending(true);
      await AxiosInstance().post(
        "/notifications/admin/no-care-plan-monthly/send",
        { jobIds },
      );
      toast.success("Notifications sent successfully.");
      setUpcomingSelectedJobIdsByRowId({});
      setUpcomingRefetchNonce((n) => n + 1);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ??
        e?.response?.data?.error ??
        e?.message ??
        "Could not send notifications.";
      toast.error(typeof msg === "string" ? msg : "Could not send notifications.");
    } finally {
      setUpcomingBulkSending(false);
    }
  }

  useEffect(() => {
    if (tab !== "email" || emailTab !== "completed") return;
    let isMounted = true;

    const fetchEmailLogs = async () => {
      try {
        setEmailLoading(true);
        const params = new URLSearchParams({
          page: String(emailLogPage),
          limit: String(emailLogLimit),
          status: "",
        });
        if (emailRecipientType !== "all") {
          params.set("recipientType", emailRecipientType);
        }
        if (debouncedEmailSearch) params.set("search", debouncedEmailSearch);
        const { data }: any = await AxiosInstance().get(
          `/notifications/admin/logs/email?${params.toString()}`,
        );
        const { items, totalPages } = extractNotificationPagedItems(
          data,
          emailLogLimit,
          emailLogPage,
        );
        const parsedRows = items.map((log: any) =>
          mapNotificationLogToRow(log, "email"),
        );
        if (isMounted) {
          setEmailRows(parsedRows);
          setEmailLogTotalPages(totalPages);
          setEmailLogPage((p) => Math.min(p, totalPages));
        }
      } catch {
        if (isMounted) {
          setEmailRows([]);
          setEmailLogTotalPages(1);
        }
      } finally {
        if (isMounted) setEmailLoading(false);
      }
    };

    void fetchEmailLogs();
    return () => {
      isMounted = false;
    };
  }, [
    tab,
    emailTab,
    emailRecipientType,
    emailLogPage,
    emailLogLimit,
    debouncedEmailSearch,
  ]);

  useEffect(() => {
    if (tab !== "sms" || smsTab !== "sent") return;
    let isMounted = true;

    const fetchSmsLogs = async () => {
      try {
        setSmsLoading(true);
        const params = new URLSearchParams({
          page: String(smsLogPage),
          limit: String(smsLogLimit),
        });
        if (smsRecipientType !== "all") {
          params.set("recipientType", smsRecipientType);
        }
        if (debouncedSmsSearch) params.set("search", debouncedSmsSearch);
        const { data }: any = await AxiosInstance().get(
          `/notifications/admin/logs/sms?${params.toString()}`,
        );
        const { items, totalPages } = extractNotificationPagedItems(
          data,
          smsLogLimit,
          smsLogPage,
        );
        const parsedRows = items.map((log: any) =>
          mapNotificationLogToRow(log, "sms"),
        );
        if (isMounted) {
          setSmsRows(parsedRows);
          setSmsLogTotalPages(totalPages);
          setSmsLogPage((p) => Math.min(p, totalPages));
        }
      } catch {
        if (isMounted) {
          setSmsRows([]);
          setSmsLogTotalPages(1);
        }
      } finally {
        if (isMounted) setSmsLoading(false);
      }
    };

    void fetchSmsLogs();
    return () => {
      isMounted = false;
    };
  }, [
    tab,
    smsTab,
    smsLogPage,
    smsLogLimit,
    smsRecipientType,
    debouncedSmsSearch,
  ]);

  useEffect(() => {
    if (tab !== "email" || emailTab !== "unsubscribed") return;
    let isMounted = true;

    const fetchEmailUnsubscribes = async () => {
      try {
        setEmailUnsubscribeLoading(true);
        const params = new URLSearchParams({
          page: String(emailUnsubPage),
          limit: String(emailUnsubLimit),
          channel: "email",
        });
        if (debouncedEmailSearch) params.set("search", debouncedEmailSearch);
        const { data }: any = await AxiosInstance().get(
          `/notifications/admin/email-unsubscribes?${params.toString()}`,
        );
        const { items, totalPages } = extractNotificationPagedItems(
          data,
          emailUnsubLimit,
          emailUnsubPage,
        );
        const parsed = items.map(mapUnsubscribeAuditLog);
        if (isMounted) {
          setEmailUnsubscribeRows(parsed);
          setEmailUnsubTotalPages(totalPages);
          setEmailUnsubPage((p) => Math.min(p, totalPages));
        }
      } catch {
        if (isMounted) {
          setEmailUnsubscribeRows([]);
          setEmailUnsubTotalPages(1);
        }
      } finally {
        if (isMounted) setEmailUnsubscribeLoading(false);
      }
    };

    void fetchEmailUnsubscribes();
    return () => {
      isMounted = false;
    };
  }, [
    tab,
    emailTab,
    emailUnsubPage,
    emailUnsubLimit,
    debouncedEmailSearch,
    emailUnsubRefetchNonce,
  ]);

  useEffect(() => {
    if (tab !== "sms" || smsTab !== "unsubscribed") return;
    let isMounted = true;

    const fetchSmsUnsubscribes = async () => {
      try {
        setSmsUnsubscribeLoading(true);
        const params = new URLSearchParams({
          page: String(smsUnsubPage),
          limit: String(smsUnsubLimit),
          channel: "sms",
        });
        if (debouncedSmsSearch) params.set("search", debouncedSmsSearch);
        const { data }: any = await AxiosInstance().get(
          `/notifications/admin/email-unsubscribes?${params.toString()}`,
        );
        const { items, totalPages } = extractNotificationPagedItems(
          data,
          smsUnsubLimit,
          smsUnsubPage,
        );
        const parsed = items.map(mapUnsubscribeAuditLog);
        if (isMounted) {
          setSmsUnsubscribeRows(parsed);
          setSmsUnsubTotalPages(totalPages);
          setSmsUnsubPage((p) => Math.min(p, totalPages));
        }
      } catch {
        if (isMounted) {
          setSmsUnsubscribeRows([]);
          setSmsUnsubTotalPages(1);
        }
      } finally {
        if (isMounted) setSmsUnsubscribeLoading(false);
      }
    };

    void fetchSmsUnsubscribes();
    return () => {
      isMounted = false;
    };
  }, [
    tab,
    smsTab,
    smsUnsubPage,
    smsUnsubLimit,
    debouncedSmsSearch,
    smsUnsubRefetchNonce,
  ]);

  useEffect(() => {
    if (tab !== "upcoming") return;
    let cancelled = false;

    if (!(isAdmin || isAdminManager) && !selectedOrgId) {
      setUpcomingRows([]);
      setUpcomingTotalPages(1);
      setUpcomingLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const load = async () => {
      try {
        setUpcomingLoading(true);
        const params = new URLSearchParams({
          page: String(upcomingPage),
          limit: String(upcomingLimit),
          cycle: upcomingCycle,
        });
        if (isAdmin || isAdminManager) {
          if (effectiveUpcomingOrgIds.length > 0) {
            params.set("organizationId", effectiveUpcomingOrgIds.join(","));
          }
        } else if (selectedOrgId && selectedOrgId !== "anthro-care") {
          params.set("organizationId", selectedOrgId);
        }
        if (debouncedUpcomingSearch) params.set("search", debouncedUpcomingSearch);
        const { data }: any = await AxiosInstance().get(
          `/notifications/admin/no-care-plan-monthly/pending?${params.toString()}`,
        );
      
        const { items: list, totalPages } = extractNotificationPagedItems(
          data,
          upcomingLimit,
          upcomingPage,
        );

        const rows = list.map((item: any, i: number) =>
          mapCarePlanMonthlyPending(item, i),
        );
        if (!cancelled) {
          // console.log("storing all data in upcoming rows");
          // console.log("rows -> ", rows);
          setUpcomingRows(rows);
          setUpcomingTotalPages(totalPages);
          setUpcomingPage((p) => Math.min(p, totalPages));
        }
      } catch {
        if (!cancelled) {
          setUpcomingRows([]);
          setUpcomingTotalPages(1);
        }
      } finally {
        if (!cancelled) setUpcomingLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [
    tab,
    upcomingCycle,
    selectedOrgId,
    isAdmin,
    isAdminManager,
    effectiveUpcomingOrgIds,
    upcomingPage,
    upcomingLimit,
    debouncedUpcomingSearch,
    upcomingRefetchNonce,
  ]);

  const activeScopedSearch =
    tab === "email"
      ? emailSearch
      : tab === "sms"
        ? smsSearch
        : tab === "upcoming"
          ? upcomingSearch
          : "";
  const setActiveScopedSearch =
    tab === "email"
      ? setEmailSearch
      : tab === "sms"
        ? setSmsSearch
        : tab === "upcoming"
          ? setUpcomingSearch
          : () => {};

  return (
    <DashboardLayout activePage="/notifications">
      <div className="">
        <div className="mb-4">
          <ToggleGroup
            type="single"
            value={tab}
            onValueChange={(value) => {
              if (value) setTab(value as "email" | "sms" | "upcoming");
            }}
            className="mb-4 teachers flex w-full sm:w-auto"
          >
            <ToggleGroupItem
              value="email"
              aria-label="Email"
              style={{
                backgroundColor: tab === "email" ? "#3463AA" : "#EEEEEE",
              }}
              className={`px-6 py-2 text-sm flex-1 md:flex-initial font-medium ${tab === "email" ? "bg-primary-dark !important text-white" : "bg-grey-light"}`}
            >
              Email
            </ToggleGroupItem>
            <ToggleGroupItem
              value="sms"
              aria-label="SMS"
              style={{
                backgroundColor: tab === "sms" ? "#3463AA" : "#EEEEEE",
              }}
              className={`px-6 py-2 text-sm flex-1 md:flex-initial font-medium ${tab === "sms" ? "bg-primary-dark !important text-white" : "bg-grey-light"}`}
            >
              SMS
            </ToggleGroupItem>
            <ToggleGroupItem
              value="upcoming"
              aria-label="Upcoming Reports"
              style={{
                backgroundColor: tab === "upcoming" ? "#3463AA" : "#EEEEEE",
              }}
              className={`px-6 py-2 text-sm flex-1 md:flex-initial font-medium ${tab === "upcoming" ? "bg-primary-dark !important text-white" : "bg-grey-light"}`}
            >
              Queued
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        {tab === "email" ? (
          <>
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between w-full min-w-0 mb-4">
              <div className="relative w-full md:basis-1/2 md:w-64">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  value={activeScopedSearch}
                  onChange={(e) => setActiveScopedSearch(e.target.value)}
                  placeholder={
                    emailTab === "unsubscribed"
                      ? "Search by Name, ID, Email, Organization"
                      : "Search by Name, ID, Email, Organization"
                  }
                  className="pl-9 pr-9 ignore"
                />
                {activeScopedSearch ? (
                  <button
                    type="button"
                    onClick={() => setActiveScopedSearch("")}
                    className="absolute right-3 top-3 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-4 md:mb-0 md:ml-auto md:justify-end w-full md:w-auto">
                <Select
                  value={emailTab}
                  onValueChange={(v) => setEmailTab(v as "completed" | "unsubscribed")}
                >
                  <SelectTrigger className={notificationsFilterSelectTriggerClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                  </SelectContent>
                </Select>
                {emailTab === "completed" ? (
                  <RecipientTypeFilterSelect
                    value={emailRecipientType}
                    onValueChange={setEmailRecipientType}
                  />
                ) : null}
              </div>
            </div>
            {emailTab === "completed" ? (
              <div className="mt-0 space-y-4">
                {emailLoading ? (
                  // <EmptyState text="Loading email logs..." />
                  <div className={tableShellClass}>
                  <TableSkeleton rows={10} columns={6} />
                </div>
                ) : emailRows.length === 0 ? (
                  <EmptyState text="No completed emails found." />
                ) : (
                  <NotificationTable
                    rows={emailRows}
                    actionsVariant="email"
                    user={user}
                    profile={profile}
                  />
                )}
                {!emailLoading || true ? (
                  <div className="px-2">
                    <Pagination
                      page={emailLogPage}
                      setPage={setEmailLogPage}
                      limit={emailLogLimit}
                      setLimit={setEmailLogLimit}
                      total={emailLogTotalPages}
                    />
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="mt-0 space-y-4">
                {emailUnsubscribeLoading ? (
                  // <EmptyState text="Loading email unsubscribes..." />
                  <div className={tableShellClass}>
                    <TableSkeleton rows={10} columns={6} />
                  </div>
                ) : (
                  <EmailUnsubscribedTable
                    rows={emailUnsubscribeRows}
                    user={user}
                    profile={profile}
                    onReverseSuccess={() =>
                      setEmailUnsubRefetchNonce((n) => n + 1)
                    }
                  />
                )}
                {!emailUnsubscribeLoading ? (
                  <div className="px-2">
                    <Pagination
                      page={emailUnsubPage}
                      setPage={setEmailUnsubPage}
                      limit={emailUnsubLimit}
                      setLimit={setEmailUnsubLimit}
                      total={emailUnsubTotalPages}
                    />
                  </div>
                ) : null}
              </div>
            )}
          </>
        ) : null}

        {tab === "sms" ? (
          <>
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between w-full min-w-0 mb-4">
              <div className="relative w-full md:basis-1/2 md:w-64">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  value={activeScopedSearch}
                  onChange={(e) => setActiveScopedSearch(e.target.value)}
                  placeholder={
                    smsTab === "unsubscribed"
                      ? " Search by Name, ID, number, Organization"
                      : " Search by Name, ID, number, Organization"
                  }
                  className="pl-9 pr-9 ignore"
                />
                {activeScopedSearch ? (
                  <button
                    type="button"
                    onClick={() => setActiveScopedSearch("")}
                    className="absolute right-3 top-3 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-4 md:mb-0 md:ml-auto md:justify-end w-full md:w-auto">
                <Select
                  value={smsTab}
                  onValueChange={(v) => setSmsTab(v as "sent" | "unsubscribed")}
                >
                  <SelectTrigger className={notificationsFilterSelectTriggerClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                  </SelectContent>
                </Select>
                {smsTab === "sent" ? (
                  <RecipientTypeFilterSelect
                    value={smsRecipientType}
                    onValueChange={setSmsRecipientType}
                  />
                ) : null}
              </div>
            </div>
            {smsTab === "sent" ? (
              <div className="mt-0 space-y-4">
                {smsLoading ? (
                  // <EmptyState text="Loading SMS logs..." />
                  <div className={tableShellClass}>
                  <TableSkeleton rows={10} columns={6} />
                </div>
                ) : smsRows.length === 0 ? (
                  <EmptyState text="No sent SMS found." />
                ) : (
                  <NotificationTable
                    rows={smsRows}
                    actionsVariant="sms"
                    user={user}
                    profile={profile}
                  />
                )}
                {!smsLoading || true ? (
                  <div className="px-2">
                    <Pagination
                      page={smsLogPage}
                      setPage={setSmsLogPage}
                      limit={smsLogLimit}
                      setLimit={setSmsLogLimit}
                      total={smsLogTotalPages}
                    />
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="mt-0 space-y-4">
                {smsUnsubscribeLoading ? (
                  // <EmptyState text="Loading SMS unsubscribes..." />
                  <div className={tableShellClass}>
                    <TableSkeleton rows={10} columns={6} />
                  </div>
                ) : (
                  <SmsUnsubscribedTable
                    rows={smsUnsubscribeRows}
                    user={user}
                    profile={profile}
                    onReverseSuccess={() =>
                      setSmsUnsubRefetchNonce((n) => n + 1)
                    }
                  />
                )}
                {!smsUnsubscribeLoading ? (
                  <div className="px-2">
                    <Pagination
                      page={smsUnsubPage}
                      setPage={setSmsUnsubPage}
                      limit={smsUnsubLimit}
                      setLimit={setSmsUnsubLimit}
                      total={smsUnsubTotalPages}
                    />
                  </div>
                ) : null}
              </div>
            )}
          </>
        ) : null}

        {tab === "upcoming" ? (
          <>
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between w-full min-w-0 mb-4">
              <div className="relative w-full md:basis-1/2 md:w-64">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  value={upcomingSearch}
                  onChange={(e) => setUpcomingSearch(e.target.value)}
                  placeholder="Search by Name, ID, Organization"
                  className="pl-9 pr-9 ignore"
                />
                {upcomingSearch ? (
                  <button
                    type="button"
                    onClick={() => setUpcomingSearch("")}
                    className="absolute right-3 top-3 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              <div className="flex  items-center gap-3 mb-4 md:mb-0 md:ml-auto md:justify-end w-full md:w-auto">
              {( isAdmin || isAdminManager ) ? (
                <AssignMultiSelect
                placeholder="Select Organization"
                options={organizations.filter((org) => String(org?._id) !== "anthro-care")
                    .map((org) => ({
                      label: org?.organizationName ?? org?.name ?? String(org?._id),
                      key: String(org?._id),
                    }))}
                className="h-9 rounded-md border border-[#6A92E8] bg-transparent px-3 text-sm teachers text-[#3D6FD9] transition-colors"
                selectedValues={upcomingTriggerOrgIdsDraft}
                onSelectedValuesChange={setUpcomingTriggerOrgIdsDraft}
                resetOnDone={false}
                applyOnClearAll
                handleSelectionDone={(v: string[]) => {
                  setUpcomingTriggerOrgIdsDraft(v);
                  setUpcomingTriggerOrgIds(v);
                  setUpcomingPage(1);
                }}
              />
              ) : null}

              {( isAdmin || isAdminManager ) ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0 border-primary text-primary"
                      aria-label="Monthly schedule"
                      onClick={() => setNoCarePlanScheduleOpen(true)}
                    >
                      <CalendarClock className="h-4 w-4" aria-hidden />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Monthly schedule</TooltipContent>
                </Tooltip>
              ) : null}
              {/* <Select
                value={upcomingCycle}
                onValueChange={setUpcomingCycle}
                disabled={upcomingLoading}
              >
                <SelectTrigger
                  id="notif-upcoming-cycle"
                  aria-label="Billing cycle month"
                  className={cn(
                    notificationsFilterSelectTriggerClass,
                    "w-[min(100%,10rem)] max-w-[10rem] shrink-0 disabled:opacity-60 md:w-[10rem]",
                  )}
                >
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {upcomingCycleMonthOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select> */}
              </div>
            </div>
            {upcomingLoading ? (
              // <EmptyState text="Loading Upcoming Reports…" />
              <div className={tableShellClass}>
                <TableSkeleton rows={10} columns={6} />
              </div>
            ) : !(isAdmin || isAdminManager) && !selectedOrgId ? (
              <EmptyState text="Select an organization to load upcoming notifications." />
            ) : (
              <div className="space-y-4">
                <UpcomingCombinedTable
                  rows={upcomingRows}
                  onAbortCarePlanMonthlyJob={abortUpcomingCarePlanMonthlyJob}
                  onSendCarePlanMonthlyJobs={sendUpcomingCarePlanMonthlyJobs}
                  isBulkSending={upcomingBulkSending}
                  selectedJobIdsByRowId={upcomingSelectedJobIdsByRowId}
                  onSelectedJobIdsByRowIdChange={setUpcomingSelectedJobIdsByRowId}
                  showCheckBoxColumn={(isAdmin || isAdminManager) ? true : false}
                />
                <div className="px-2">
                  <Pagination
                    page={upcomingPage}
                    setPage={setUpcomingPage}
                    limit={upcomingLimit}
                    setLimit={setUpcomingLimit}
                    total={upcomingTotalPages}
                  />
                </div>
              </div>
            )}
          </>
        ) : null}

        <NoCarePlanMonthlyScheduleDialog
          open={noCarePlanScheduleOpen}
          onOpenChange={setNoCarePlanScheduleOpen}
        />
      </div>
    </DashboardLayout>
  );
}

function NotificationTable({
  rows,
  actionsVariant,
  compact = false,
  user,
  profile,
}: {
  rows: NotificationRow[];
  actionsVariant: "sms" | "email";
  compact?: boolean;
  user: { role?: string; id?: string; caretakerId?: string | null } | undefined;
  profile: { _id?: string; caretakerId?: string } | null | undefined;
}) {
  const isEmailCompleted = actionsVariant === "email";
  const isSmsSent = actionsVariant === "sms";
  //  console.log( "rowsx", rows);
  const statusClass = (status: string) => {
  if (!status) return "";
  const s = status?.toLowerCase();
  if (s === "sent") return "capitalize border border-[#34C759] bg-[#E8F9EE] text-[#34C759]"; // keep current look
  if (s === "failed") return "capitalize border border-[#FF3B30] bg-[#FFE5E5] text-[#FF3B30]";
  return "capitalize border border-[#808080] bg-[#E5E5E5] text-[#808080]";
};

  return (
    <div className={tableShellClass}>
      <Table>
        <TableHeader>
          <TableRow className={tableHeaderRowClass}>
            <TableHead className={tableHeadClass}>ID</TableHead>
            <TableHead className={tableHeadClass}>Name</TableHead>

            {isEmailCompleted ? (
              <TableHead
                className={clsx(
                  tableHeadClass,
                  compact && "hidden xl:table-cell",
                )}
              >
                Email
              </TableHead>
            ) : null}

            {isSmsSent ? (
              <TableHead className={tableHeadClass}>Phone</TableHead>
            ) : null}
            <TableHead
              className={clsx(
                tableHeadClass,
                compact && "hidden xl:table-cell",
              )}
            >
              Organisation
            </TableHead>
            {isEmailCompleted ? (
              <TableHead
                className={clsx(
                  tableHeadClass,
                  compact && "hidden xl:table-cell",
                )}
              >
                Trigger
              </TableHead>
            ) : null}
            <TableHead
              className={clsx(
                tableHeadClass,
                compact && "hidden xl:table-cell",
              )}
            >
              Timezone
            </TableHead>
            {/* {isEmailCompleted ? (
              <TableHead className={tableHeadClass}>Email Variables</TableHead>
            ) : null} */}
            {isSmsSent ? (
              <TableHead className={tableHeadClass}>Trigger</TableHead>
            ) : null}
            {isEmailCompleted ? (
              <TableHead
                className={clsx(
                  tableHeadClass,
                  compact && "hidden xl:table-cell",
                )}
              >
                Recipient Type
              </TableHead>
            ) : isSmsSent ? (
              <TableHead
                className={clsx(
                  tableHeadClass,
                  compact && "hidden xl:table-cell",
                )}
              >
                SMS Body
              </TableHead>
            ) : null}
            <TableHead className={tableHeadClass}>Time</TableHead>
            {isEmailCompleted ? (
              <TableHead className={tableHeadClass}>Email Variables</TableHead>
            ) : null}
            <TableHead className={tableHeadClass}>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow
              key={r.rowKey ?? `${r.patientId}-${r.timestamp}-${r.email}`}
              className={tableBodyRowClass}
            >
              <TableCell className={tableCellClass}>
                {r.detailPathname &&
                !isOwnCaretakerNotificationRow(r, user, profile) ? (
                  <Link
                    to={r.detailPathname}
                    className={tableIdLinkClass}
                  >
                    {r.patientId}
                  </Link>
                ) : (
                  isOwnCaretakerNotificationRow(r, user, profile) ? ( <Link
                    to={'/profile'}
                    className={tableIdLinkClass}
                  >
                    {r.patientId}
                  </Link>  ) : (
                  <>{r.patientId}</> )
                )}
              </TableCell>
              <TableCell className={tableCellClass + " capitalize"}>{r.name}</TableCell>
              {isEmailCompleted ? (
                <TableCell className={clsx(tableCellClass, compact && "hidden xl:table-cell")}>
                  {r?.recipientEmail ??  r?.email}
                </TableCell>
              ) : null}
              {isSmsSent ? (
                <TableCell className={tableCellClass}>
                  {formatUSPhoneNumber(r.phoneNumber) || "—"}
                </TableCell>
              ) : null}
              <TableCell className={clsx(tableCellClass + " capitalize", compact && "hidden xl:table-cell")}>
                {r.organisation}
              </TableCell>
              {isEmailCompleted ? (
                <TableCell className={clsx(tableCellClass, compact && "hidden xl:table-cell")}>
                  {r.program}
                </TableCell>
              ) : null}
              <TableCell className={clsx(tableCellClass, compact && "hidden xl:table-cell")}>
              {formatUsTimezoneLabel(r.timezone)}
              </TableCell>
              {/* {isEmailCompleted ? (
                <TableCell className={clsx(tableCellClass, compact && "hidden xl:table-cell")}>
                  <TemplateVarsCell templateVars={r.templateVars} />
                </TableCell>
              ) : null} */}
              {isSmsSent ? (
                <TableCell>{r.smsTemplateName}</TableCell>
              ) : null}
              {isEmailCompleted ? (
                <TableCell className={clsx(tableCellClass, compact && "hidden xl:table-cell")}>
                  <span className="max-w-[260px] inline-block truncate align-middle capitalize">
                    {r.recipientType ?? "—"}
                  </span>
                </TableCell>
              ) : isSmsSent ? (
                <TableCell className={clsx(tableCellClass, compact && "hidden xl:table-cell")}>
                  <SmsBodyCell smsBody={r.smsBody} />
                </TableCell>
              ) : null}
              <TableCell className={cn(tableCellClass, "whitespace-nowrap")}>
                {formatNotificationTableDateTime(r.timestamp)}
              </TableCell>
              {isEmailCompleted ? (
                <TableCell className={clsx(tableCellClass, compact && "hidden xl:table-cell")}>
                  <TemplateVarsCell templateVars={r.templateVars} />
                </TableCell>
              ) : null}
              <TableCell>
                <Badge variant="secondary" className={statusClass(r.status)}>
                  {r.status}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function UnsubscribeIdLink({
  row,
  user,
  profile,
}: {
  row: EmailUnsubscribeRow;
  user: { role?: string; id?: string; caretakerId?: string | null } | undefined;
  profile: { _id?: string; caretakerId?: string } | null | undefined;
}) {
  const rowForCheck: NotificationRecipientLinkRow = {
    patientId: row.patientId,
    detailPathname: row.detailPathname,
    recipientType: row.recipientRole,
  };

  if(isOwnCaretakerNotificationRow(rowForCheck, user, profile))
  {
    return <Link
    to={'/profile'}
    className={tableIdLinkClass}
  >
    {row.patientId}
  </Link>
  }

  if (
    !row.detailPathname
  ) {
    return <>{row.patientId}</>;
  }

  return (
    <Link
      to={row.detailPathname}
      className={tableIdLinkClass}
    >
      {row.patientId}
    </Link>
  );
}

/** Undo unsubscribe audit row (restores subscription). Same route for email and SMS audit logs. */
function UnsubscribeReverseButton({
  auditMongoId,
  onSuccess,
  channel,
}: {
  auditMongoId: string | undefined;
  onSuccess: () => void;
  channel: "email" | "sms";
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const canSubmit = Boolean(auditMongoId?.trim());
  const channelLabel = channel === "sms" ? "SMS" : "email";

  const runReverse = async () => {
    if (!auditMongoId?.trim() || loading) return;
    setLoading(true);
    try {
      const res: any = await AxiosInstance().post(
        `/notifications/email-unsubscribes/${encodeURIComponent(auditMongoId.trim())}/reverse`,
      );
      toast.success(
        res?.data?.message ||
          "Unsubscribe removed; recipient can receive messages again.",
      );
      setOpen(false);
      onSuccess();
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      toast.error(
        typeof msg === "string" ? msg : "Could not reverse this unsubscribe.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(next) => !loading && setOpen(next)}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!canSubmit || loading}
        // className="border-destructive/30 text-destructive hover:bg-destructive/10"
        onClick={() => canSubmit && setOpen(true)}
      >
        Remove
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove</AlertDialogTitle>
          <AlertDialogDescription>
          Are you sure you want to remove this item from unsubscribe?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel type="button" disabled={loading}>
            No
          </AlertDialogCancel>
          <Button
            type="button"
            disabled={loading}
            // className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => void runReverse()}
          >
            {loading ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : null}
            Remove
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EmailUnsubscribedTable({
  rows,
  onReverseSuccess,
  user,
  profile,
}: {
  rows: EmailUnsubscribeRow[];
  onReverseSuccess: () => void;
  user: { role?: string; id?: string; caretakerId?: string | null } | undefined;
  profile: { _id?: string; caretakerId?: string } | null | undefined;
}) {
  if (rows.length === 0) {
    return (
      <EmptyState text="No email unsubscribes found." />
    );
  }

  return (
    <div className={tableShellClass}>
      <Table>
        <TableHeader>
          <TableRow className={tableHeaderRowClass}>
            <TableHead className={tableHeadClass}>ID</TableHead>
            <TableHead className={tableHeadClass}>Name</TableHead>
            <TableHead className={tableHeadClass}>Email</TableHead>
            <TableHead className={tableHeadClass}>
              Time of Unsubscription
            </TableHead>
            <TableHead className={tableHeadClass}>Organisations</TableHead>

            {/* <TableHead className={tableHeadClass}>Template variables</TableHead> */}
            <TableHead className={tableHeadClass}>Status</TableHead>
            <TableHead className={cn(tableHeadClass, "text-right")}>
              Action
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow
              key={r.id}
              className={tableBodyRowClass}
            >
              <TableCell className={tableCellClass}>
                <UnsubscribeIdLink row={r} user={user} profile={profile} />
              </TableCell>
              <TableCell className={tableCellClass + ' capitalize'}>{r.name}</TableCell>
              <TableCell className={tableCellClass}>{r.email}</TableCell>
              <TableCell className={tableCellClass}>
                {formatNotificationTableDateTime(r.unsubscribedAt)}
              </TableCell>
              <TableCell className={tableCellClass + ' capitalize'}>{r.organisation}</TableCell>
              {/* <TableCell>
                <TemplateVarsCell templateVars={r.templateVars} />
              </TableCell> */}
              <TableCell>
                <Badge variant="secondary" className="capitalize">
                  {r.status}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <UnsubscribeReverseButton
                  auditMongoId={r.auditMongoId}
                  channel="email"
                  onSuccess={onReverseSuccess}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SmsUnsubscribedTable({
  rows,
  onReverseSuccess,
  user,
  profile,
}: {
  rows: EmailUnsubscribeRow[];
  onReverseSuccess: () => void;
  user: { role?: string; id?: string; caretakerId?: string | null } | undefined;
  profile: { _id?: string; caretakerId?: string } | null | undefined;
}) {
  if (rows.length === 0) {
    return (
      <EmptyState text="No SMS unsubscribes found." />
    );
  }

  return (
    <div className={tableShellClass}>
      <Table>
        <TableHeader>
          <TableRow className={tableHeaderRowClass}>
            <TableHead className={tableHeadClass}>ID</TableHead>
            <TableHead className={tableHeadClass}>Name</TableHead>
            <TableHead className={tableHeadClass}>Phone</TableHead>
            <TableHead className={tableHeadClass}>
              Time of Unsubscription
            </TableHead>
            <TableHead className={tableHeadClass}>Organisation</TableHead>
            <TableHead className={tableHeadClass}>Status</TableHead>
            <TableHead className={cn(tableHeadClass, "text-right")}>
              Action
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} className={tableBodyRowClass}>
              <TableCell className={tableCellClass}>
                <UnsubscribeIdLink row={r} user={user} profile={profile} />
              </TableCell>
              <TableCell className={tableCellClass}>{r.name}</TableCell>
              <TableCell className={tableCellClass}>
                {formatUSPhoneNumber(r.phone ?? "") || "—"}
              </TableCell>
              <TableCell className={tableCellClass}>
                {formatNotificationTableDateTime(r.unsubscribedAt)}
              </TableCell>
              <TableCell className={tableCellClass}>{r.organisation}</TableCell>
              <TableCell>
                <Badge variant="secondary" className="capitalize">
                  {r.status}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <UnsubscribeReverseButton
                  auditMongoId={r.auditMongoId}
                  channel="sms"
                  onSuccess={onReverseSuccess}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const UPCOMING_ABORT_ACCENT = "#7290d0";

/** Upcoming row: abort care-plan monthly job via POST (parent runs API + toast). */
function UpcomingAbortButton({
  row,
  abortingRowId,
  setAbortingRowId,
  onAbort,
  onRequestAbort,
}: {
  row: UpcomingRow;
  abortingRowId: string | null;
  setAbortingRowId: (id: string | null) => void;
  onAbort: (row: UpcomingRow) => Promise<void>;
  /** When set, the primary click opens confirmation instead of calling `onAbort` immediately. */
  onRequestAbort?: (row: UpcomingRow) => void;
}) {
  const jobId = row.abortJobId;
  const thisRowLoading = abortingRowId === row.id;
  const anyRowLoading = abortingRowId !== null;

  return (
    <div className="flex justify-end">
      <button
        type="button"
        disabled={!jobId || (anyRowLoading && !thisRowLoading)}
        aria-busy={thisRowLoading}
        onClick={() => {
          if (!jobId || anyRowLoading) return;
          if (onRequestAbort) {
            onRequestAbort(row);
            return;
          }
          void (async () => {
            setAbortingRowId(row.id);
            try {
              await onAbort(row);
            } finally {
              setAbortingRowId(null);
            }
          })();
        }}
        className={cn(
          "inline-flex h-8 min-w-[5.5rem] items-center justify-center gap-1.5 rounded-md border px-3 text-xs font-semibold transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          jobId
            ? "border-[#7290d0] bg-background text-[#7290d0] hover:bg-[#7290d0]/12 active:bg-[#7290d0]/18 focus-visible:ring-[#7290d0]/35"
            : "cursor-not-allowed border-border bg-muted/50 text-muted-foreground",
          anyRowLoading && !thisRowLoading && false && "opacity-45",
          thisRowLoading && "pointer-events-none cursor-wait",
        )}
        title={
          jobId
            ? "Abort notification job (care-plan-monthly)"
            : "No job ID for this row"
        }
      >
        {thisRowLoading && false ? (
          <>
            <Loader2
              className="size-3.5 shrink-0 animate-spin"
              style={{ color: UPCOMING_ABORT_ACCENT }}
              aria-hidden
            />
            <span>Aborting…</span>
          </>
        ) : (
          "Remove"
        )}
      </button>
    </div>
  );
}

type UpcomingDetailRow = { label: string; value: string };

type UpcomingDetailsView =
  | { layout: "single"; title: string; rows: UpcomingDetailRow[] }
  | {
      layout: "dual";
      title: string;
      email: { title: string; rows: UpcomingDetailRow[] };
      sms: { title: string; rows: UpcomingDetailRow[] };
    };

function scheduledForDetailRow(
  extraData: UpcomingDetailRow[],
): UpcomingDetailRow {
  return (
    extraData.find((d) => d.label === "Scheduled for") ?? {
      label: "Scheduled for",
      value: "—",
    }
  );
}

function buildUpcomingEmailDetailRows(
  r: UpcomingRow,
  extraData: UpcomingDetailRow[],
): UpcomingDetailRow[] {
  return [
    // { label: "Delivery status", value: r.deliveryStatus ?? "—" },
    { label: "Email", value: r.email ?? "—" },
    { label: "Trigger", value: "No_Care_Plan_Monthly" },
    // { label: "Email type", value: r.emailType ?? "—" },  
    // { label: "Email Variables", value: "-" },
    scheduledForDetailRow(extraData),
  ];
}

function buildUpcomingSmsDetailRows(
  r: UpcomingRow,
  extraData: UpcomingDetailRow[],
): UpcomingDetailRow[] {
  return [
    // { label: "Delivery Status", value: r.deliveryStatus ?? "—" },
    { label: "Phone no", value: r.phoneNo ?? "—" },
    { label: "Trigger", value: "No_Care_Plan_Monthly" },
    // { label: "SMS Content", value: r.smsContent ?? "—" },
    scheduledForDetailRow(extraData),
  ];
}

function getUpcomingChannelDetails(r: UpcomingRow): UpcomingDetailsView {
  const extraData = r.expandedDetails ?? [];

  if (r.channel === "alert") {
    return {
      layout: "single",
      title: "Critical reading details",
      rows: [...(r.expandedDetails ?? [])],
    };
  }

  if (r.channel === "email,sms") {
    return {
      layout: "dual",
      title: "Email & SMS details",
      email: {
        title: "Email details",
        rows: buildUpcomingEmailDetailRows(r, extraData),
      },
      sms: {
        title: "SMS details",
        rows: buildUpcomingSmsDetailRows(r, extraData),
      },
    };
  }

  if (r.channel === "email") {
    return {
      layout: "single",
      title: "Email details",
      rows: buildUpcomingEmailDetailRows(r, extraData),
    };
  }

  return {
    layout: "single",
    title: "SMS details",
    rows: buildUpcomingSmsDetailRows(r, extraData),
  };
}

function UpcomingDetailRowsList({
  rowId,
  rows,
  sectionKey,
}: {
  rowId: string;
  rows: UpcomingDetailRow[];
  sectionKey: string;
}) {
  return (
    <div className="divide-y ">
      {rows.map((d, idx) => (
        <div
          key={`${rowId}-${sectionKey}-${idx}`}
          className="flex min-w-0 flex-wrap items-baseline gap-x-1 p-4"
        >
          <span className="shrink-0 text-sm font-semibold teachers text-tertiary">
            {d.label}:
          </span>
          <span className="min-w-0 flex-1 wrap-break-word text-sm text-tertiary font-[500] teachers">
            {formatUpcomingDetailCell(d.label, d.value || "")}
          </span>
        </div>
      ))}
    </div>
  );
}

function UpcomingCombinedTable({
  rows,
  onAbortCarePlanMonthlyJob,
  onSendCarePlanMonthlyJobs,
  isBulkSending,
  selectedJobIdsByRowId,
  onSelectedJobIdsByRowIdChange,
  showCheckBoxColumn = false,
}: {
  rows: UpcomingRow[];
  onAbortCarePlanMonthlyJob: (row: UpcomingRow) => Promise<void>;
  onSendCarePlanMonthlyJobs: (jobIds: string[]) => Promise<void>;
  isBulkSending: boolean;
  selectedJobIdsByRowId: Record<string, string>;
  onSelectedJobIdsByRowIdChange: (
    updater: (prev: Record<string, string>) => Record<string, string>,
  ) => void;
  showCheckBoxColumn: boolean;
}) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [abortingRowId, setAbortingRowId] = useState<string | null>(null);
  const [abortConfirmRow, setAbortConfirmRow] = useState<UpcomingRow | null>(null);
  const [triggerConfirmOpen, setTriggerConfirmOpen] = useState(false);
  const toggleExpanded = (rowId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const abortDialogLoading =
    abortConfirmRow != null && abortingRowId === abortConfirmRow.id;

  const selectableRows = rows.filter((row) => {
    const jobId = row.abortJobId ?? row.id;
    return Boolean(jobId && jobId.trim());
  });

  const selectedCount = Object.keys(selectedJobIdsByRowId).length;
  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => Boolean(selectedJobIdsByRowId[row.id]));

  const toggleSelectRow = (rowId: string, next: boolean) => {
    const row = rows.find((r) => r.id === rowId);
    const rowJobId = row?.abortJobId ?? row?.id;
    if (!rowJobId || !rowJobId.trim()) return;
    onSelectedJobIdsByRowIdChange((prev) => {
      const updated = { ...prev };
      if (next) updated[rowId] = rowJobId;
      else delete updated[rowId];
      return updated;
    });
  };

  const toggleSelectAll = (next: boolean) => {
        onSelectedJobIdsByRowIdChange((prev) => {
      const updated = { ...prev };
      if (!next) {
        // for (const row of selectableRows) delete updated[row.id];
        return {} as Record<string, string>;
      }
      for (const row of selectableRows) {
        const jobId = row.abortJobId ?? row.id;
        if (jobId && jobId.trim()) updated[row.id] = jobId;
      }
      return updated;
    });
  };

  const runBulkSend = () => {
    const selectedJobIds = Object.values(selectedJobIdsByRowId).filter(
      (jobId): jobId is string => Boolean(jobId && jobId.trim()),
    );
    if (!selectedJobIds.length || isBulkSending) return;
    setTriggerConfirmOpen(true);
  };

  const runConfirmedBulkSend = () => {
    const selectedJobIds = Object.values(selectedJobIdsByRowId).filter(
      (jobId): jobId is string => Boolean(jobId && jobId.trim()),
    );
    if (!selectedJobIds.length || isBulkSending) return;
    void (async () => {
      await onSendCarePlanMonthlyJobs(selectedJobIds);
      setTriggerConfirmOpen(false);
    })();
  };

  if (!rows.length) {
    return (
      <EmptyState text="No pending care-plan monthly notifications for this cycle." />
    );
  }

  const runConfirmedAbort = () => {
    if (!abortConfirmRow) return;
    const row = abortConfirmRow;
    void (async () => {
      setAbortingRowId(row.id);
      try {
        await onAbortCarePlanMonthlyJob(row);
      } finally {
        setAbortingRowId(null);
        setAbortConfirmRow(null);
      }
    })();
  };

  return (
  <>
    {selectedCount > 0 ? (
      <Button
        type="button"
        size="sm"
        className="mb-2 h-9 rounded-full border border-[#CFD2EB] bg-[#E8E9F3] px-3 py-1 text-sm teachers text-black transition-colors hover:bg-[#D9DEF6] hover:border-[#B8C4F0]"
        disabled={isBulkSending}
        onClick={runBulkSend}
      >
        <span className="font-normal">{isBulkSending ? "Sending..." : `Send Notifications (${selectedCount})`}</span>
        {!isBulkSending ? (
          <span
            role="button"
            aria-label="Clear selected patients"
            tabIndex={0}
            className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:bg-[#CBD4F5] hover:text-black"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleSelectAll(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                toggleSelectAll(false);
              }
            }}
          >
            <X className="h-3 w-3" aria-hidden />
          </span>
        ) : null}
      </Button>
    ) : null}
    <div className={tableShellClass}>
      {/* <div className="flex items-center justify-between gap-3 border-b px-3 py-2">
        <p className="text-sm text-muted-foreground teachers">
          {selectedCount > 0
            ? `${selectedCount} selected`
            : "Select pending jobs to trigger notifications."}
        </p>
        {selectedCount > 0 ? (
          <Button
            type="button"
            size="sm"
            disabled={isBulkSending}
            onClick={runBulkSend}
          >
            {isBulkSending ? "Triggering..." : "Trigger Selected"}
          </Button>
        ) : null}
      </div> */}

      <Table>
        <TableHeader>
          <TableRow className={tableHeaderRowClass}>
            <TableHead
              className={cn(tableHeadClass, "w-[44px] border-0 shadow-none")}
            ></TableHead>
            {showCheckBoxColumn && <TableHead className={cn(tableHeadClass, "w-[44px]")}>
              <Checkbox
                checked={allSelected}
                onCheckedChange={(checked) => toggleSelectAll(Boolean(checked))}
                aria-label="Select all pending jobs"
              />
            </TableHead>}
            <TableHead className={tableHeadClass}>ID</TableHead>
            <TableHead className={tableHeadClass}>Patient Name</TableHead>
            <TableHead className={tableHeadClass}>Timezone</TableHead>
            <TableHead className={tableHeadClass}>Organisation</TableHead>
            <TableHead className={tableHeadClass}>Program</TableHead>
            <TableHead className={tableHeadClass}>Channel</TableHead>
            <TableHead className={cn(tableHeadClass, "text-right")}>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const isExpanded = expandedIds.has(r.id);
            const details = getUpcomingChannelDetails(r);
            // Expand should always be available because channel-specific details exist.
            const hasDetails = true;
            const rowJobId = r.abortJobId ?? r.id;
            const canSelect = Boolean(rowJobId && rowJobId.trim());
            const isSelected = Boolean(selectedJobIdsByRowId[r.id]);
            return (
              <Fragment key={r.id}>
                <TableRow className={tableBodyRowClass}>
                  <TableCell className="align-middle border-0">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(r.id)}
                      disabled={!hasDetails}
                      className={cn(
                        "flex h-8 w-8 items-center justify-center rounded-md border-0 bg-transparent transition-colors",
                        hasDetails
                          ? "hover:bg-muted"
                          : "opacity-40 cursor-not-allowed",
                      )}
                      aria-label={isExpanded ? "Collapse row" : "Expand row"}
                      title={hasDetails ? (isExpanded ? "Collapse" : "Expand") : "No details"}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>
                  </TableCell>
                  {showCheckBoxColumn && <TableCell className="align-middle">
                    <Checkbox
                      checked={isSelected}
                      disabled={!canSelect || isBulkSending}
                      onCheckedChange={(checked) =>
                        toggleSelectRow(r.id, Boolean(checked))
                      }
                      aria-label={`Select ${r.patientName}`}
                    />
                  </TableCell>}
                  <TableCell className={tableCellClass}>
                    {r.patientProfilePath ? (
                      <Link
                        to={r.patientProfilePath}
                        className={tableIdLinkClass}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {r.patientId}
                      </Link>
                    ) : (
                      r.patientId
                    )}
                  </TableCell>
                  <TableCell className={tableCellClass}>{r.patientName}</TableCell>
                  <TableCell className={tableCellClass}>
                    {formatUsTimezoneLabel(r.timezone)}
                  </TableCell>
                  <TableCell className={tableCellClass}>{r.organisation}</TableCell>
                  <TableCell className={tableCellClass}>{r.program}</TableCell>
                  <TableCell>
                    {r.channel === "email" ? (
                      <Badge variant="default" className="gap-1">
                        {/* <Mail className="h-3 w-3" /> */}
                        Email
                      </Badge>
                    ) : r.channel === "sms" ? (
                      <Badge variant="secondary" className="gap-1 border-border">
                        {/* <MessageSquare className="h-3 w-3" /> */}
                        SMS
                      </Badge>
                    ) 
                    : r.channel === "email,sms" ? (
                      <div className="flex gap-1">
                      <Badge variant="default" className="gap-1">
                        {/* <Mail className="h-3 w-3" /> */}
                        Email
                      </Badge>
                      <Badge variant="secondary" className="gap-1">
                        {/* <Mail className="h-3 w-3" /> */}
                        SMS
                      </Badge>
                      </div>
                    )
                    : (
                      <Badge variant="destructive" className="gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Alert
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right align-middle">
                    <UpcomingAbortButton
                      row={r}
                      abortingRowId={abortingRowId}
                      setAbortingRowId={setAbortingRowId}
                      onAbort={onAbortCarePlanMonthlyJob}
                      onRequestAbort={setAbortConfirmRow}
                    />
                  </TableCell>
                </TableRow>

                {isExpanded && (
                  <TableRow className="bg-muted/20 hover:bg-muted/20">
                    <TableCell colSpan={9} className="p-0">
                      <div className="p-4">
                        <div className="rounded-lg border border-border bg-background">
                          {/* <div className="border-b border-border px-4 py-3">
                            <div className="teachers text-sm font-semibold text-foreground">
                              {details.title}
                            </div>
                          </div> */}
                          <div className="max-h-[280px] overflow-y-auto">
                            {details.layout === "dual" ? (
                              <div className="grid grid-cols-1 md:grid-cols-2">
                                <div className="min-w-0">
                                  <div className="border-b px-4 py-3 text-sm font-semibold teachers text-tertiary">
                                    {details.email.title}
                                  </div>
                                  <UpcomingDetailRowsList
                                    rowId={r.id}
                                    rows={details.email.rows}
                                    sectionKey="email"
                                  />
                                </div>
                                <div className="min-w-0">
                                  <div className="border-b px-4 py-3 text-sm font-semibold teachers text-tertiary">
                                    {details.sms.title}
                                  </div>
                                  <UpcomingDetailRowsList
                                    rowId={r.id}
                                    rows={details.sms.rows}
                                    sectionKey="sms"
                                  />
                                </div>
                              </div>
                            ) : (
                              <>
                              <div className="border-b px-4 py-3 text-sm font-semibold teachers text-tertiary">
                                {details.title}
                              </div>
                              <UpcomingDetailRowsList
                                rowId={r.id}
                                rows={details.rows}
                                sectionKey="single"
                              />
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>

      <AlertDialog
        open={abortConfirmRow !== null}
        onOpenChange={(next) => {
          if (!next && abortDialogLoading) return;
          if (!next) setAbortConfirmRow(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove</AlertDialogTitle>
            <AlertDialogDescription>
              {abortConfirmRow ? (
                <>
                 Are you sure you want to remove this item?
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button" disabled={abortDialogLoading}>
              Cancel
            </AlertDialogCancel>
            <Button
              type="button"
              disabled={abortDialogLoading}
              // className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => runConfirmedAbort()}
            >
              {abortDialogLoading ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : null}
              Remove
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={triggerConfirmOpen}
        onOpenChange={(next) => {
          if (!next && isBulkSending) return;
          setTriggerConfirmOpen(next);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Notifications</AlertDialogTitle>
            <AlertDialogDescription>
            Are you sure you want to send notifications ?    
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button" disabled={isBulkSending}>
              Cancel
            </AlertDialogCancel>
            <Button
              type="button"
              disabled={isBulkSending}
              onClick={() => runConfirmedBulkSend()}
            >
              {isBulkSending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : null}
              Send 
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </>
  );
}

